// Direct-message lib — same-team-only 1:1 chat between students.
// Server-side schema lives in dm_threads + dm_messages with RLS that
// requires both participants to still be on the team. All thread-mutation
// paths go through SECURITY DEFINER RPCs so the client doesn\'t need write
// access to dm_threads.

import type { RealtimeChannel } from "@supabase/supabase-js";

import { supabase } from "./supabase";

export type DmReplyTo = {
  id: string;
  sender_id: string;
  sender_name: string | null;
  body_preview: string;
  deleted: boolean;
};

export type DmMessage = {
  id: string;
  thread_id: string;
  sender_id: string;
  body: string;
  created_at: string;
  /** Display name resolved from user_profiles. */
  sender_name: string | null;
  sender_avatar_url: string | null;
  /** Threaded reply target — null when not a reply. */
  reply_to_message_id: string | null;
  reply_to: DmReplyTo | null;
  /** Soft-delete tombstone. UI renders these as a grey ghost row. */
  deleted_at: string | null;
  deleted_by_user_id: string | null;
  deleted_by_name: string | null;
};

type RawDmMessage = {
  id: string;
  thread_id: string;
  sender_id: string;
  body: string;
  created_at: string;
  reply_to_message_id: string | null;
  deleted_at: string | null;
  deleted_by_user_id: string | null;
};

const DM_SELECT_COLS =
  "id, thread_id, sender_id, body, created_at, reply_to_message_id, deleted_at, deleted_by_user_id";

// ===========================================================================
// Thread bootstrap
// ===========================================================================

/**
 * Open the DM thread between the current user and `otherUserId` on whatever
 * team they share. Creates the thread if it doesn\'t exist yet. The RPC
 * enforces the same-team rule and orders the (member_a, member_b) pair so
 * we never end up with two threads for the same pair.
 */
export async function openOrCreateDmThread(otherUserId: string): Promise<{ threadId: string | null; error: Error | null }> {
  const { data, error } = await supabase.rpc("open_or_create_dm_thread", {
    p_other_user_id: otherUserId,
  });
  if (error) return { threadId: null, error: new Error(error.message) };
  return { threadId: typeof data === "string" ? data : null, error: null };
}

// ===========================================================================
// Messages
// ===========================================================================

/** Most recent N DM messages for a thread, oldest-first. Hydrates reply
 *  parents + deleter names so the UI can render quoted previews and
 *  "Message deleted by X" without an N+1 round-trip per row. */
export async function listDmMessages(threadId: string, limit = 200): Promise<DmMessage[]> {
  const { data, error } = await supabase
    .from("dm_messages")
    .select(DM_SELECT_COLS)
    .eq("thread_id", threadId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error || !data) return [];
  const rows = (data as RawDmMessage[]).reverse();
  return enrichWithSenders(rows);
}

async function fetchDmMessageById(id: string): Promise<DmMessage | null> {
  const { data, error } = await supabase
    .from("dm_messages")
    .select(DM_SELECT_COLS)
    .eq("id", id)
    .maybeSingle();
  if (error || !data) return null;
  const enriched = await enrichWithSenders([data as RawDmMessage]);
  return enriched[0] ?? null;
}

async function enrichWithSenders(rows: RawDmMessage[]): Promise<DmMessage[]> {
  if (rows.length === 0) return [];

  // Reply-parent fetch (parents that aren't already in the same window).
  const inWindow = new Map(rows.map((r) => [r.id, r] as const));
  const parentIdsToFetch = new Set<string>();
  for (const r of rows) {
    if (r.reply_to_message_id && !inWindow.has(r.reply_to_message_id)) {
      parentIdsToFetch.add(r.reply_to_message_id);
    }
  }
  let parentRows: RawDmMessage[] = [];
  if (parentIdsToFetch.size > 0) {
    const { data: parents } = await supabase
      .from("dm_messages")
      .select(DM_SELECT_COLS)
      .in("id", [...parentIdsToFetch]);
    parentRows = (parents ?? []) as RawDmMessage[];
  }
  const parentById = new Map<string, RawDmMessage>();
  for (const p of parentRows) parentById.set(p.id, p);

  const ids = new Set<string>();
  for (const r of rows) {
    ids.add(r.sender_id);
    if (r.deleted_by_user_id) ids.add(r.deleted_by_user_id);
  }
  for (const p of parentRows) ids.add(p.sender_id);

  const { data: profiles } = await supabase
    .from("user_profiles")
    .select("id, full_name, avatar_url")
    .in("id", [...ids]);
  const byId = new Map<string, { name: string | null; avatar: string | null }>();
  for (const p of (profiles ?? []) as Array<{ id: string; full_name: string | null; avatar_url: string | null }>) {
    byId.set(p.id, { name: p.full_name, avatar: p.avatar_url });
  }

  function buildReplyPreview(parentId: string): DmReplyTo {
    const parent = parentById.get(parentId) ?? inWindow.get(parentId);
    if (!parent) {
      return { id: parentId, sender_id: "", sender_name: null, body_preview: "", deleted: true };
    }
    const isDeleted = parent.deleted_at != null;
    return {
      id: parent.id,
      sender_id: parent.sender_id,
      sender_name: byId.get(parent.sender_id)?.name ?? null,
      body_preview: isDeleted
        ? ""
        : (parent.body ?? "").slice(0, 140).replace(/\s+/g, " ").trim(),
      deleted: isDeleted,
    };
  }

  return rows.map((r) => ({
    ...r,
    sender_name: byId.get(r.sender_id)?.name ?? null,
    sender_avatar_url: byId.get(r.sender_id)?.avatar ?? null,
    reply_to: r.reply_to_message_id ? buildReplyPreview(r.reply_to_message_id) : null,
    deleted_by_name: r.deleted_by_user_id ? byId.get(r.deleted_by_user_id)?.name ?? null : null,
  }));
}

/** Send a message in a thread. RLS requires the current user to be a participant. */
export async function sendDmMessage(
  threadId: string,
  body: string,
  options?: { replyToMessageId?: string | null }
): Promise<{ message: DmMessage | null; error: Error | null }> {
  const { data: userRes } = await supabase.auth.getUser();
  const uid = userRes.user?.id;
  if (!uid) return { message: null, error: new Error("not authenticated") };
  const trimmed = body.trim();
  if (!trimmed) return { message: null, error: new Error("empty body") };

  const insert: Record<string, unknown> = {
    thread_id: threadId,
    sender_id: uid,
    body: trimmed,
  };
  if (options?.replyToMessageId) insert.reply_to_message_id = options.replyToMessageId;

  const { data, error } = await supabase
    .from("dm_messages")
    .insert(insert)
    .select(DM_SELECT_COLS)
    .maybeSingle();
  if (error || !data) return { message: null, error: error ? new Error(error.message) : new Error("insert failed") };
  const enriched = await enrichWithSenders([data as RawDmMessage]);
  return { message: enriched[0] ?? null, error: null };
}

/** Soft-delete a DM. Server-side only the sender can delete their own. */
export async function deleteDmMessage(messageId: string): Promise<{ ok: boolean; error: Error | null }> {
  const { error } = await supabase.rpc("delete_dm_message", { p_message_id: messageId });
  if (error) return { ok: false, error: new Error(error.message) };
  return { ok: true, error: null };
}

// ===========================================================================
// Read cursor
// ===========================================================================

/** Move my read cursor on this thread forward to `iso`. Idempotent server-side. */
export async function markDmRead(threadId: string, iso: string): Promise<void> {
  const { error } = await supabase.rpc("mark_dm_read", {
    p_thread_id: threadId,
    p_iso: iso,
  });
  if (error && typeof console !== "undefined") {
    console.warn("[markDmRead] failed:", error.message);
  }
}

// ===========================================================================
// Unread aggregation
// ===========================================================================

/** Total unread across every live DM thread for the current user. */
export async function getMyTotalDmUnread(): Promise<number> {
  const { data, error } = await supabase.rpc("get_my_total_dm_unread");
  if (error || data == null) return 0;
  const n = typeof data === "number" ? data : Number(data);
  return Number.isFinite(n) ? n : 0;
}

/** Per-thread unread for the current user. */
export async function getMyDmUnreadByThread(): Promise<Map<string, number>> {
  const { data, error } = await supabase.rpc("get_my_dm_unread_by_thread");
  const out = new Map<string, number>();
  if (error || !Array.isArray(data)) return out;
  for (const row of data as Array<{ thread_id: string; unread: number }>) {
    out.set(row.thread_id, Number(row.unread) || 0);
  }
  return out;
}

// ===========================================================================

/** Live-stream new dm_messages for a single thread. */
export function subscribeToDmMessages(
  threadId: string,
  onInsert: (m: DmMessage) => void
): () => void {
  const channel: RealtimeChannel = supabase
    .channel(`dm-messages:${threadId}`)
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "dm_messages",
        filter: `thread_id=eq.${threadId}`,
      },
      (payload) => {
        const id = (payload.new as { id?: string } | null)?.id;
        if (!id) return;
        void fetchDmMessageById(id).then((m) => {
          if (m) onInsert(m);
        });
      }
    )
    .subscribe();
  return () => {
    try {
      void supabase.removeChannel(channel);
    } catch {
      // already removed
    }
  };
}

/**
 * Shell-level subscription that fires whenever something has happened that
 * could change my DM unread totals: any new dm_message, OR any dm_thread
 * UPDATE (last_read_at moved, or left_at was set). Caller re-fetches the
 * unread count when this fires. RLS scopes both subscriptions for free.
 */
export function subscribeToDmUnreadSignals(onChange: () => void): () => void {
  const channel = supabase
    .channel("dm-unread-signals")
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "dm_messages" },
      () => onChange()
    )
    .on(
      "postgres_changes",
      { event: "UPDATE", schema: "public", table: "dm_threads" },
      () => onChange()
    )
    .subscribe();
  return () => {
    try {
      void supabase.removeChannel(channel);
    } catch {
      // already removed
    }
  };
}

/**
 * Per-thread snapshot for one team — used to drive the roster's per-member
 * unread badges. Returns an entry for every live thread the current user is
 * a participant in on this team.
 */
export type DmThreadForTeam = {
  threadId: string;
  otherUserId: string;
  unread: number;
  lastMessageAt: string | null;
};

export async function getMyDmThreadsForTeam(teamId: string): Promise<DmThreadForTeam[]> {
  const { data, error } = await supabase.rpc("get_my_dm_threads_for_team", {
    p_team_id: teamId,
  });
  if (error || !Array.isArray(data)) return [];
  return (data as Array<{ thread_id: string; other_user_id: string; unread: number; last_message_at: string | null }>).map(
    (r) => ({
      threadId: r.thread_id,
      otherUserId: r.other_user_id,
      unread: Number(r.unread) || 0,
      lastMessageAt: r.last_message_at ?? null,
    })
  );
}

/** My current last_read_at on this thread. Snapshot once on entry to drive the unread divider. */
export async function getMyDmLastReadAt(threadId: string): Promise<string | null> {
  const { data, error } = await supabase.rpc("get_my_dm_last_read_at", {
    p_thread_id: threadId,
  });
  if (error || !data) return null;
  return typeof data === "string" ? data : null;
}

